// @ts-nocheck
/**
 * View Blacklist / DND — dual tab, server-paged (?page&limit[&mobile]) DataTable,
 * full export (?export=1), confirm before delete. Blacklist delete via
 * deleteApiCallWithHeader; DND remove via POST DND_ADD operation:'remove'.
 */
import { useState, useEffect, useMemo } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Trash2, Plus, Search as SearchIcon, Ban, PhoneOff, RefreshCw } from 'lucide-react';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { deleteApiCallWithHeader } from '@/service/deleteApiCall';
import {
  API_DND_LIST,
  API_DND_ADD,
  API_BLACKLIST_LIST,
  API_BLACKLIST_DELETE,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { ROUTES, USER_ROLES_MAP, NUMBER_OPS_CREATE_ROLES, NUMBER_OPS_DND_ROLES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { downloadCsv, stampedFileName } from '@/components/data-table/csv-export';

const EXPORT_LIMIT = 100000; // backend cap for ?export=1

const formatDate = (val: any) => (val ? new Date(val).toLocaleString() : '—');
const creatorName = (u: any) => (u ? u.name || u.username || `#${u.id}` : '—');
const creatorRole = (u: any) => (u?.role ? USER_ROLES_MAP[u.role] || u.role : '');
const centreNames = (row: any) =>
  (row.call_centres || []).map((c: any) => c.name || `#${c.id}`).join(', ');

const listUrl = (tab: string, page: number, limit: number, mobile: string, isExport = false) => {
  const base = tab === 'dnd' ? API_DND_LIST : API_BLACKLIST_LIST(getUser()?.id);
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (mobile) qs.set('mobile', mobile);
  if (isExport) qs.set('export', '1');
  return `${base}?${qs}`;
};

const ViewBlacklistDnd = () => {
  const role = getUser()?.role;
  const canCreate = NUMBER_OPS_CREATE_ROLES.includes(role);
  const canDnd = NUMBER_OPS_DND_ROLES.includes(role);

  const [activeTab, setActiveTab] = usePersistedState('number-operations/ViewBlacklistDnd:activeTab', 'blacklist');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number | null>>({ blacklist: null, dnd: null });
  const [paging, setPaging] = usePersistedState('number-operations/ViewBlacklistDnd:paging', { pageIndex: 0, pageSize: 25 });
  const [mobileInput, setMobileInput] = usePersistedState('number-operations/ViewBlacklistDnd:mobileInput', '');
  const [mobile, setMobile] = useState(() => (mobileInput.length === 10 ? mobileInput : ''));
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Exact-mobile filter: applies once a full 10 digits is typed, clears when emptied.
  useEffect(() => {
    const t = setTimeout(() => {
      const v = mobileInput.length === 10 ? mobileInput : '';
      if (v !== mobile) {
        setMobile(v);
        setPaging((p) => ({ ...p, pageIndex: 0 }));
      }
    }, 350);
    return () => clearTimeout(t);
  }, [mobileInput, mobile]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getApiCall(listUrl(activeTab, paging.pageIndex + 1, paging.pageSize, mobile), {})
      .then((data: any) => {
        if (cancelled) return;
        const count = Number(data?.total) || 0;
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(count);
        if (!mobile) setCounts((c) => ({ ...c, [activeTab]: count }));
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (typeof err === 'string' && err.trim()) toast.error(friendlyApiError(err) || 'Failed to fetch list');
        setRows([]);
        setTotal(0);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activeTab, paging, mobile, reloadKey]);

  // DND tab badge count without opening the tab.
  useEffect(() => {
    if (!canDnd) return;
    getApiCall(listUrl('dnd', 1, 1, ''), {})
      .then((d: any) => setCounts((c) => ({ ...c, dnd: Number(d?.total) || 0 })))
      .catch(() => {});
  }, [canDnd, reloadKey]);

  const switchTab = (key: string) => {
    if (key === activeTab) return;
    setActiveTab(key);
    setRows([]);
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };

  const confirmDelete = async () => {
    const item = pendingDelete;
    if (!item) return;
    setDeleting(true);
    try {
      if (activeTab === 'dnd') {
        await postApiCall(API_DND_ADD, { mobile: item.mobile, operation: 'remove' });
        toast.success('Number removed from DND list');
      } else {
        await deleteApiCallWithHeader(API_BLACKLIST_DELETE(item.id));
        toast.success('Number removed from Blacklist');
      }
      setPendingDelete(null);
      // Removed the last row of a later page → step back one page.
      if (rows.length === 1 && paging.pageIndex > 0) setPaging((p) => ({ ...p, pageIndex: p.pageIndex - 1 }));
      else setReloadKey((k) => k + 1);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const data: any = await getApiCall(listUrl(activeTab, 1, EXPORT_LIMIT, mobile, true), {});
      const list = Array.isArray(data?.rows) ? data.rows : [];
      if (!list.length) {
        toast.info('Nothing to export');
        return;
      }
      const isBl = activeTab === 'blacklist';
      const header = ['ID', 'Mobile Number', ...(isBl ? ['Call Centres'] : []), 'Created By', 'Creator Role', 'Created At', 'Updated At'];
      const lines = list.map((r: any) => [
        r.id,
        r.mobile,
        ...(isBl ? [centreNames(r)] : []),
        creatorName(r.created_by_user),
        creatorRole(r.created_by_user),
        formatDate(r.created_at),
        formatDate(r.updated_at),
      ]);
      downloadCsv([header, ...lines], stampedFileName(isBl ? 'blacklist' : 'dnd'));
      if (Number(data?.total) > list.length) {
        toast.warning(`Exported first ${list.length.toLocaleString()} of ${Number(data.total).toLocaleString()} rows`);
      }
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      { accessorKey: 'id', header: 'ID', cell: ({ getValue }) => <span className="font-mono text-[11px] text-muted-foreground">{getValue()}</span> },
      { accessorKey: 'mobile', header: 'Mobile Number', cell: ({ getValue }) => <span className="font-mono text-sm tracking-wider">{getValue()}</span> },
    ];
    if (activeTab === 'blacklist') {
      cols.push({ id: 'call_centres', header: 'Call Centres', accessorFn: centreNames, cell: ({ getValue }) => getValue() || '—' });
    }
    cols.push(
      {
        id: 'created_by',
        header: 'Created By',
        accessorFn: (r) => creatorName(r.created_by_user),
        cell: ({ row }) => (
          <div className="leading-tight">
            <div className="text-foreground">{creatorName(row.original.created_by_user)}</div>
            {creatorRole(row.original.created_by_user) && (
              <div className="text-[10px] text-muted-foreground">{creatorRole(row.original.created_by_user)}</div>
            )}
          </div>
        ),
      },
      { accessorKey: 'created_at', header: 'Created At', cell: ({ getValue }) => formatDate(getValue()) },
      { accessorKey: 'updated_at', header: 'Updated At', cell: ({ getValue }) => formatDate(getValue()) },
      {
        id: 'actions',
        header: 'Action',
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <button
            type="button"
            title={`Remove from ${activeTab === 'dnd' ? 'DND' : 'Blacklist'}`}
            aria-label={`Remove ${row.original.mobile}`}
            onClick={(e) => {
              e.stopPropagation();
              setPendingDelete(row.original);
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        ),
      },
    );
    return cols;
  }, [activeTab]);

  const tabs = [
    { key: 'blacklist', label: 'Blacklist', icon: Ban, tone: 'rose' },
    ...(canDnd ? [{ key: 'dnd', label: 'DND List', icon: PhoneOff, tone: 'amber' }] : []),
  ];

  return (
    <FillPage>
      <PageHeader
        title="Blacklist / DND"
        description="Numbers currently on the Blacklist or DND list. Add via Manage."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              disabled={loading}
              onClick={() => setReloadKey((k) => k + 1)}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.NUMBER_OPERATIONS.SEARCH_DND_BLIST}`}>
                <SearchIcon className="h-4 w-4" />
                Search
              </Link>
            </Button>
            {canCreate && (
              <Button asChild>
                <Link to={`/${ROUTES.NUMBER_OPERATIONS.MANAGE_BLACKLIST_DND}`}>
                  <Plus className="h-4 w-4" />
                  Add number
                </Link>
              </Button>
            )}
          </>
        }
      />

      <div className="mb-3 flex gap-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          const activeCls =
            tab.tone === 'rose'
              ? 'border-rose-500 bg-rose-500 text-white shadow-md ring-2 ring-rose-500/25'
              : 'border-amber-500 bg-amber-500 text-white shadow-md ring-2 ring-amber-500/25';
          const idleCls =
            tab.tone === 'rose'
              ? 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-400 hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300'
              : 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300';
          const count = counts[tab.key];
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => switchTab(tab.key)}
              className={
                'relative flex h-10 items-center gap-2 rounded-lg border-2 px-4 text-sm font-bold transition ' +
                (active ? activeCls : idleCls)
              }
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              <span
                className={
                  'ml-1 inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ' +
                  (active
                    ? 'bg-white/90 text-foreground'
                    : tab.tone === 'rose'
                      ? 'bg-rose-200/70 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200'
                      : 'bg-amber-200/70 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200')
                }
              >
                {count == null ? '…' : count.toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      <DataTable
        key={activeTab}
        tableId={`number-ops.${activeTab}`}
        columns={columns}
        data={rows}
        isLoading={loading}
        pageSize={25}
        serverPagination={{ ...paging, rowCount: total, onChange: setPaging }}
        getRowId={(r) => String(r.id)}
        disableSearch
        leftContent={
          <Input
            className="h-8 w-56 font-mono"
            inputMode="numeric"
            placeholder="Filter by 10-digit mobile"
            value={mobileInput}
            onChange={(e) => setMobileInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
          />
        }
        emptyTitle={mobile ? 'No match for this number' : 'No numbers found'}
        onExport={handleExport}
        exportLabel={exporting ? 'Exporting…' : 'Export'}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}
        title={`Remove from ${activeTab === 'dnd' ? 'DND' : 'Blacklist'}?`}
        description={
          pendingDelete
            ? `${pendingDelete.mobile} will be removed from the ${activeTab === 'dnd' ? 'DND list' : 'Blacklist'}. This cannot be undone.`
            : ''
        }
        confirmLabel="Remove"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />
    </FillPage>
  );
};

export default ViewBlacklistDnd;
